import { Fragment } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { CabecalhoPublico, Marca } from "../componentes/Marca.js";
import { MetadadosPagina } from "../componentes/Metadados.js";
import { ARTIGOS_BLOG, artigoPorSlug, type ArtigoBlog } from "./artigos.js";
import "./blog.css";

const ROTULOS_FUNIL: Record<ArtigoBlog["etapaFunil"], string> = {
  descoberta: "Entender",
  consideracao: "Organizar",
  conversao: "Escolher",
  ativacao_retencao: "Usar melhor",
};

function RodapeBlog() {
  return (
    <footer className="blog__rodape">
      <div>
        <Marca reloadDocument />
        <p>Você aluga. O resto é com a gente.</p>
      </div>
      <nav aria-label="Links do rodapé">
        <Link reloadDocument to="/">Início</Link>
        <a href="/manual/">Manual</a>
        <Link reloadDocument to="/calculadora-imposto-aluguel">Calculadora</Link>
        <Link reloadDocument to="/termos">Termos</Link>
        <Link reloadDocument to="/privacidade">Privacidade</Link>
      </nav>
    </footer>
  );
}

export function BlogIndice() {
  return (
    <div className="blog">
      <MetadadosPagina caminho="/blog" />
      <CabecalhoPublico reloadDocument acao={<Link reloadDocument className="blog__acao-topo" to="/painel">Entrar</Link>} />
      <main>
        <header className="blog__apresentacao">
          <span className="rotulo">Blog do Alugando.com</span>
          <h1>Aluguel em ordem, sem complicação</h1>
          <p>
            Guias práticos para acompanhar recebimentos, organizar documentos e entender
            as decisões de cada mês.
          </p>
        </header>
        <section className="blog__lista" aria-label="Artigos">
          {ARTIGOS_BLOG.map((artigo) => (
            <article className="blog__cartao" key={artigo.id}>
              <Link reloadDocument to={`/blog/${artigo.slug}`} aria-label={`Ler ${artigo.titulo}`}>
                <img src={artigo.imagem.src} width={artigo.imagem.largura}
                  height={artigo.imagem.altura} alt="" loading="lazy" />
              </Link>
              <div>
                <p className="blog__meta">
                  <span>{artigo.categoria}</span>
                  <span>{ROTULOS_FUNIL[artigo.etapaFunil]}</span>
                  <span>{artigo.tempoLeitura}</span>
                </p>
                <h2><Link reloadDocument to={`/blog/${artigo.slug}`}>{artigo.titulo}</Link></h2>
                <p>{artigo.resumo}</p>
                <Link reloadDocument className="blog__ler" to={`/blog/${artigo.slug}`}>Ler artigo</Link>
              </div>
            </article>
          ))}
        </section>
      </main>
      <RodapeBlog />
    </div>
  );
}

export function BlogArtigo({ slug }: { slug?: string }) {
  const parametros = useParams<{ slug: string }>();
  const artigo = artigoPorSlug(slug ?? parametros.slug ?? "");
  if (!artigo) return <Navigate to="/blog" replace />;
  const caminho = `/blog/${artigo.slug}` as const;
  return (
    <div className="blog blog--artigo">
      <MetadadosPagina caminho={caminho} />
      <CabecalhoPublico reloadDocument acao={<Link reloadDocument className="blog__acao-topo" to="/blog">Todos os artigos</Link>} />
      <main>
        <article className="artigo">
          <nav className="artigo__migalhas" aria-label="Navegação estrutural">
            <Link reloadDocument to="/">Início</Link><span aria-hidden="true">/</span>
            <Link reloadDocument to="/blog">Blog</Link><span aria-hidden="true">/</span>
            <span>{artigo.categoria}</span>
          </nav>
          <header className="artigo__cabecalho">
            <p className="blog__meta">
              <span>{artigo.categoria}</span>
              <span>{ROTULOS_FUNIL[artigo.etapaFunil]}</span>
              <span>{artigo.tempoLeitura}</span>
            </p>
            <h1>{artigo.titulo}</h1>
            <p className="artigo__resumo">{artigo.resumo}</p>
            <div className="artigo__autoria">
              <span>Por {artigo.autor.nome}</span>
              <span>Publicado em <time dateTime={artigo.publicadoEm}>
                {new Date(`${artigo.publicadoEm}T12:00:00Z`).toLocaleDateString("pt-BR")}
              </time></span>
              <span>Revisado em <time dateTime={artigo.revisadoEm}>
                {new Date(`${artigo.revisadoEm}T12:00:00Z`).toLocaleDateString("pt-BR")}
              </time></span>
            </div>
          </header>
          <figure className="artigo__capa">
            <img src={artigo.imagem.src} width={artigo.imagem.largura}
              height={artigo.imagem.altura} alt={artigo.imagem.alt} />
          </figure>
          <div className="artigo__corpo">
            {artigo.secoes.map((secao, indice) => (
              <Fragment key={secao.titulo}><section>
                <h2>{secao.titulo}</h2>
                {secao.paragrafos.map((paragrafo) => <p key={paragrafo}>{paragrafo}</p>)}
                {!!secao.itens?.length && (
                  <ul>{secao.itens.map((item) => <li key={item}>{item}</li>)}</ul>
                )}
                {secao.paragrafosAposItens?.map((paragrafo) => <p key={paragrafo}>{paragrafo}</p>)}
                {!!secao.links?.length && <ul>{secao.links.map((link) => (
                  <li key={link.href}><a href={link.href}>{link.rotulo}</a></li>
                ))}</ul>}
              </section>
              {artigo.imagemInterna?.aposSecao === indice + 1 && <figure className="artigo__ilustracao">
                <img src={artigo.imagemInterna.src} width={artigo.imagemInterna.largura} height={artigo.imagemInterna.altura} alt={artigo.imagemInterna.alt} loading="lazy" />
                {artigo.imagemInterna.legenda && <figcaption>{artigo.imagemInterna.legenda}</figcaption>}
              </figure>}
              </Fragment>
            ))}
          </div>
          <aside className="artigo__cta">
            <div><span className="rotulo">Próximo passo</span><h2>{artigo.chamada.titulo}</h2></div>
            <p>{artigo.chamada.texto}</p>
            {/* O carregamento completo preserva o scroll nativo para fragmentos da landing. */}
            <a href={artigo.chamada.href}>{artigo.chamada.rotulo}</a>
          </aside>
          <footer className="artigo__revisao">
            <strong>Sobre esta revisão</strong>
            <p>
              Conteúdo revisado por {artigo.revisor.nome}, {artigo.revisor.cargo.toLowerCase()}.
              Se uma regra fiscal afetar este guia, a data acima será atualizada junto com o texto.
            </p>
          </footer>
        </article>
      </main>
      <RodapeBlog />
    </div>
  );
}
